import { Heart, Shield, Target, Users } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

export function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About Us - AI Literacy Education | Promptliteracy.ai</title>
        <meta name="description" content="Learn about Promptliteracy.ai, our mission to teach AI literacy and prompt engineering skills to learners of all ages. Discover our educational approach and values." />
        
        {/* Open Graph */}
        <meta property="og:title" content="About Us - AI Literacy Education | Promptliteracy.ai" />
        <meta property="og:description" content="Learn about Promptliteracy.ai, our mission to teach AI literacy and prompt engineering skills to learners of all ages." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://promptliteracy.ai/about" />
        <meta property="og:site_name" content="Promptliteracy.ai" />
        
        {/* Additional SEO */}
        <meta name="keywords" content="about promptliteracy, AI education mission, educational values, AI literacy organization, prompt engineering education" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://promptliteracy.ai/about" />
      </Helmet>

      {/* Header Section */}
            <section className="py-16 bg-primary text-white shadow-lg">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Users className="h-12 w-12 text-secondary" />
              <h1 className="text-4xl font-bold">About Us</h1>
            </div>
            <p className="text-xl opacity-90 leading-relaxed">
              Empowering learners of all ages with essential AI literacy and prompt engineering skills
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-primary/3 relative overflow-hidden shadow-lg">
        <div className="absolute inset-0 circuit-pattern opacity-10"></div>
        <div className="container relative z-10">
        {/* Mission Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="prose max-w-none text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Our Mission</h2>
            <p className="text-lg text-foreground/80 leading-relaxed">
              AI and prompt literacy are rapidly evolving, becoming essential skills in our everyday lives. 
              PromptLiteracy.ai exists to help kids, teens, and lifelong learners understand and master the art of communicating with AI. 
              Our vision is a world where we don't just consume AI, but communicate with it clearly, shape it responsibly, and use it creatively and effectively.
            </p>
          </div>

          {/* Values Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <Card className="text-center card-slate hover-lift border-primary/20 hover:border-primary/40 transition-all card-accent">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 border border-primary/20">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg text-primary">Inclusive</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  AI literacy for learners of all ages, from kids to lifelong learners
                </p>
              </CardContent>
            </Card>

            <Card className="text-center card-slate hover-lift border-secondary/20 hover:border-secondary/40 transition-all card-accent">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4 border border-secondary/20">
                  <Target className="h-6 w-6 text-secondary" />
                </div>
                <CardTitle className="text-lg text-primary">Practical</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Real-world examples and actionable strategies you can use today
                </p>
              </CardContent>
            </Card>

            <Card className="text-center card-slate hover-lift border-primary/30 hover:border-primary/50 transition-all card-accent">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mb-4 border border-primary/30">
                  <Heart className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg text-primary">Ethical</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Responsible AI use with emphasis on safety and ethical considerations
                </p>
              </CardContent>
            </Card>

            <Card className="text-center card-slate hover-lift border-secondary/30 hover:border-secondary/50 transition-all card-accent">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-secondary/20 rounded-lg flex items-center justify-center mb-4 border border-secondary/30">
                  <Shield className="h-6 w-6 text-secondary" />
                </div>
                <CardTitle className="text-lg text-primary">Safe</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Age-appropriate content designed for safe, structured learning environments
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Editorial Standards */}
        <Card className="mb-12 card-slate border-slate-300/30 card-accent">
          <CardHeader>
            <CardTitle className="text-2xl">Editorial Standards</CardTitle>
          </CardHeader>
          <CardContent className="prose max-w-none">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold mb-3">Quality Content</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Research-backed methodologies</li>
                  <li>• Expert-curated examples</li>
                  <li>• Regular content updates</li>
                  <li>• Age-appropriate language</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3">Safety First</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Privacy-conscious practices</li>
                  <li>• Ethical AI guidance</li>
                  <li>• Safe learning environments</li>
                  <li>• Responsible technology use</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Part of Logikbug */}
        <Card className="border-primary/20 bg-primary/5 card-slate card-accent">
          <CardContent className="p-8 text-center">
            <h3 className="text-xl font-bold mb-4">Part of the Logikbug Learning Network</h3>
            <p className="text-muted-foreground mb-6">
              PromptLiteracy.ai is proudly part of Logikbug, an innovative educational technology company 
              dedicated to making complex technical concepts accessible to learners of all ages.
            </p>
            <a 
              href="https://logikbug.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:text-secondary transition-colors font-medium link-underline"
            >
              Learn more about Logikbug →
            </a>
          </CardContent>
        </Card>
        </div>
      </section>
    </>
  );
}